import { type Dispatch, type SetStateAction, useCallback, useEffect, useRef, useState } from "react";

const IS_SERVER = globalThis.window === undefined;

/**
 * Persisted state that is live per browser tab but seeded from the last value
 * written by any tab.
 *
 * `useLocalStorage` from `usehooks-ts` subscribes to the native `storage`
 * event, which fires in every other tab and window of the same browser profile.
 * Panel state stored that way means one tab yanks another tab's panels open and
 * shut — the reported symptom in B-1703, where a Claude-in-Chrome tab churning
 * through playbooks kept slamming the user's Alby panel closed.
 *
 * So: `sessionStorage` holds the live per-tab value, `localStorage` holds a seed
 * that newly opened tabs inherit, and **this hook deliberately does not listen
 * for `storage` events**. Adding such a listener would reintroduce the bug.
 *
 * The signature matches `useLocalStorage`'s first two return values, so it is a
 * drop-in swap at call sites. Values are JSON-serialized the same way, so an
 * existing `localStorage` entry is read as-is with no migration. Like
 * `useLocalStorage`, the setter has a stable identity and writes to whatever key
 * is current at call time, so consumers that capture it once (e.g. in a
 * `useEffect([])` media-query listener) keep targeting the live key after it
 * changes rather than a stale one.
 */
export const useTabScopedStorage = <T>(key: string, initialValue: T): [T, Dispatch<SetStateAction<T>>] => {
  const readValue = useCallback((): T => {
    if (IS_SERVER) {
      return initialValue;
    }

    try {
      // sessionStorage is this tab's live value; localStorage is the seed a
      // freshly opened tab inherits from whichever tab last wrote one.
      const raw = globalThis.sessionStorage.getItem(key) ?? globalThis.localStorage.getItem(key);

      return raw === null ? initialValue : (JSON.parse(raw) as T);
    } catch {
      return initialValue;
    }
  }, [initialValue, key]);

  const [storedValue, setStoredValue] = useState<T>(readValue);

  // Mirrors the latest value synchronously so updater functions resolve against
  // it. A ref, not `readValue()`: when storage is unavailable (private mode,
  // quota) the write below throws and never lands, so re-reading storage would
  // return `initialValue` every time and a toggle like `setOpen((o) => !o)`
  // would stick. A ref stays correct on that in-memory fallback path, and is
  // current even across batched updates.
  const storedValueRef = useRef(storedValue);

  // Latest key, tracked so `setValue` can stay stable (see below). Assigned
  // during render — safe because it is only read inside the event-time setter,
  // never during render output.
  const keyRef = useRef(key);
  keyRef.current = key;

  // Stable identity, matching `useLocalStorage`'s `useEventCallback` setter.
  // Empty deps on purpose: a setter that changed identity with `key` and closed
  // over it would let consumers that capture the setter once (the web sidebar's
  // `useEffect([])` media-query listener) keep writing to the key from first
  // render after `sidebarId` flips. Reading `keyRef` instead always targets the
  // live key.
  const setValue = useCallback<Dispatch<SetStateAction<T>>>((value) => {
    const activeKey = keyRef.current;
    const nextValue = typeof value === "function" ? (value as (previousValue: T) => T)(storedValueRef.current) : value;

    try {
      const serializedValue = JSON.stringify(nextValue);

      globalThis.sessionStorage.setItem(activeKey, serializedValue);
      globalThis.localStorage.setItem(activeKey, serializedValue);
    } catch {
      // Storage can be unavailable (private mode, quota). Fall back to
      // in-memory state for the life of the tab rather than throwing.
    }

    storedValueRef.current = nextValue;
    setStoredValue(nextValue);
  }, []);

  // Re-read when the key changes. Load-bearing: callers such as the web app's
  // sidebar build the key from state (`qw-sidebar-${sidebarId}-open`), so the
  // key changes as the user navigates. Keyed on `key` alone on purpose —
  // `readValue` also changes with `initialValue`, and re-reading then would
  // clobber a value the user just set.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    const value = readValue();

    storedValueRef.current = value;
    setStoredValue(value);
  }, [key]);

  return [storedValue, setValue];
};
